<template>
  <div class="relative">
    <q-inner-loading :showing="isLoading">
      <q-spinner-gears size="100px" color="primary" />
    </q-inner-loading>

    <!-- Error Banner -->
    <q-banner
      v-if="error"
      inline-actions
      rounded
      class="bg-gradient-to-r from-orange-500 to-red-500 text-white m-4 rounded-xl"
    >
      <q-icon name="error" color="white" size="2em" />
      <span class="ml-2 font-semibold">{{ error }}</span>
    </q-banner>

    <!-- Main Content -->
    <div class="p-6 space-y-6">
          <!-- Connection Status Card -->
          <div class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <q-icon name="link" class="text-white text-xl" />
              </div>
              <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">连接状态</h2>
            </div>
            <div class="flex items-center gap-3">
              <div
                :class="
                  isConnected
                    ? 'w-3 h-3 bg-green-500 rounded-full animate-pulse'
                    : 'w-3 h-3 bg-red-500 rounded-full'
                "
              ></div>
              <span
                :class="
                  isConnected
                    ? 'text-green-600 dark:text-green-400 font-semibold'
                    : 'text-red-600 dark:text-red-400 font-semibold'
                "
              >
                {{ isConnected ? "已连接" : "未连接" }}
              </span>
              <button
                @click="checkConnection"
                class="ml-auto px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm"
              >
                刷新状态
              </button>
            </div>
          </div>

          <!-- Get Property Section -->
          <div class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <q-icon name="download" class="text-white text-xl" />
              </div>
              <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">获取属性</h2>
            </div>

            <div class="space-y-4">
              <div class="flex gap-3">
                <q-select
                  v-model="selectedGetProperties"
                  :options="availableProperties"
                  label="选择属性"
                  multiple
                  emit-value
                  map-options
                  outlined
                  dense
                  use-chips
                  class="flex-1"
                  bg-color="white"
                />
                <button
                  @click="selectAllGetProperties"
                  class="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm whitespace-nowrap"
                >
                  {{ selectedGetProperties.length === availableProperties.length ? "取消全选" : "全选" }}
                </button>
              </div>
              <button
                @click="handleGetProperty"
                :disabled="!selectedGetProperties || selectedGetProperties.length === 0"
                class="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
              >
                <div class="flex items-center justify-center gap-2">
                  <q-icon name="search" />
                  <span>获取属性</span>
                </div>
              </button>

              <!-- Get Results -->
              <div v-if="getResults.length > 0" class="mt-4 space-y-2">
                <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">获取结果：</h3>
                <div
                  v-for="(result, index) in getResults"
                  :key="index"
                  class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700/50"
                >
                  <div class="flex items-center justify-between mb-2">
                    <span class="font-bold text-gray-800 dark:text-gray-200">{{ result.propertyName }}</span>
                    <span
                      :class="
                        result.status === 'OK'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      "
                      class="text-sm font-semibold"
                    >
                      {{ result.status }}
                    </span>
                  </div>
                  <div class="text-lg font-mono font-bold text-blue-700 dark:text-blue-300">
                    {{ formatValue(result.value) }}
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    时间戳: {{ new Date(result.timestamp).toLocaleString() }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Set Property Section -->
          <div class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <q-icon name="edit" class="text-white text-xl" />
              </div>
              <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">设置属性</h2>
            </div>

            <div class="space-y-4">
              <div class="flex gap-3">
                <q-select
                  v-model="selectedSetProperties"
                  :options="availableProperties"
                  label="选择属性"
                  multiple
                  emit-value
                  map-options
                  outlined
                  dense
                  use-chips
                  class="flex-1"
                  bg-color="white"
                />
                <button
                  @click="selectAllSetProperties"
                  class="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm whitespace-nowrap"
                >
                  {{ selectedSetProperties.length === availableProperties.length ? "取消全选" : "全选" }}
                </button>
              </div>
              <div class="flex gap-3">
                <q-input
                  v-model="setValueInput"
                  :label="`属性值${isMultipleSet ? '（多个值，逗号分隔）' : ''}`"
                  outlined
                  dense
                  class="flex-1"
                  bg-color="white"
                />
                <q-select
                  v-model="setValueType"
                  :options="valueTypeOptions"
                  label="值类型"
                  emit-value
                  map-options
                  outlined
                  dense
                  class="w-40"
                  bg-color="white"
                />
              </div>
              <button
                @click="handleSetProperty"
                :disabled="!selectedSetProperties || selectedSetProperties.length === 0 || !setValueInput"
                class="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
              >
                <div class="flex items-center justify-center gap-2">
                  <q-icon name="save" />
                  <span>设置属性</span>
                </div>
              </button>

              <!-- Set Results -->
              <div v-if="setResults.length > 0" class="mt-4 space-y-2">
                <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">设置结果：</h3>
                <div
                  v-for="(result, index) in setResults"
                  :key="index"
                  :class="
                    result.success
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700/50'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700/50'
                  "
                  class="rounded-lg p-4 border"
                >
                  <div class="flex items-center justify-between">
                    <span class="font-bold text-gray-800 dark:text-gray-200">{{ result.propertyName }}</span>
                    <span
                      :class="
                        result.success
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      "
                      class="text-sm font-semibold"
                    >
                      {{ result.success ? "成功" : "失败" }}
                    </span>
                  </div>
                  <div v-if="result.error" class="text-sm text-red-600 dark:text-red-400 mt-1">
                    {{ result.error }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Subscribe Section -->
          <div class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <q-icon name="notifications_active" class="text-white text-xl" />
              </div>
              <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">订阅管理</h2>
            </div>

            <div class="space-y-4">
              <div class="flex gap-3">
                <q-select
                  v-model="selectedSubscribeProperties"
                  :options="availableProperties"
                  label="选择属性"
                  multiple
                  emit-value
                  map-options
                  outlined
                  dense
                  use-chips
                  class="flex-1"
                  bg-color="white"
                />
                <button
                  @click="selectAllSubscribeProperties"
                  class="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm whitespace-nowrap"
                >
                  {{ selectedSubscribeProperties.length === availableProperties.length ? "取消全选" : "全选" }}
                </button>
              </div>
              <div class="flex gap-3">
                <q-input
                  v-model.number="updateRateMs"
                  label="更新频率 (ms)"
                  type="number"
                  outlined
                  dense
                  class="w-40"
                  bg-color="white"
                />
                <q-select
                  v-model="selectedEventFlags"
                  :options="eventFlagOptions"
                  label="事件标志（多选）"
                  multiple
                  emit-value
                  map-options
                  outlined
                  dense
                  use-chips
                  class="flex-1"
                  bg-color="white"
                />
              </div>
              <div class="flex gap-3">
                <button
                  @click="handleSubscribe"
                  :disabled="!selectedSubscribeProperties || selectedSubscribeProperties.length === 0"
                  class="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
                >
                  <div class="flex items-center justify-center gap-2">
                    <q-icon name="notifications_active" />
                    <span>订阅</span>
                  </div>
                </button>
                <button
                  @click="handleUnsubscribe"
                  :disabled="subscribedProperties.length === 0"
                  class="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
                >
                  <div class="flex items-center justify-center gap-2">
                    <q-icon name="notifications_off" />
                    <span>取消订阅</span>
                  </div>
                </button>
                <button
                  @click="handleRemoveAllListeners"
                  class="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <div class="flex items-center justify-center gap-2">
                    <q-icon name="clear_all" />
                    <span>移除所有</span>
                  </div>
                </button>
              </div>

              <!-- Subscribed Properties List -->
              <div v-if="subscribedProperties.length > 0" class="mt-4">
                <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">已订阅属性：</h3>
                <div class="flex flex-wrap gap-2">
                  <q-chip
                    v-for="prop in subscribedProperties"
                    :key="prop"
                    color="green"
                    text-color="white"
                    :label="prop"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Subscription Data Display Section -->
          <div class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <q-icon name="data_usage" class="text-white text-xl" />
                </div>
                <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">订阅数据</h2>
                <span
                  v-if="Object.keys(subscribedData).length > 0"
                  class="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-sm font-bold"
                >
                  {{ Object.keys(subscribedData).length }}
                </span>
              </div>
              <button
                @click="clearSubscriptionData"
                :disabled="Object.keys(subscribedData).length === 0"
                class="px-4 py-2 bg-gray-500 hover:bg-gray-600 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white rounded-lg text-sm"
              >
                清空数据
              </button>
            </div>

            <!-- Data List -->
            <div
              v-if="Object.keys(subscribedData).length > 0"
              class="grid grid-cols-1 gap-3 max-h-[600px] overflow-y-auto pr-2"
            >
              <div
                v-for="(data, propertyName) in subscribedData"
                :key="propertyName"
                class="group relative bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-4 border border-blue-200 dark:border-blue-700/50 hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
              >
                <div class="flex justify-between items-start mb-3">
                  <span class="font-bold text-gray-800 dark:text-gray-200 text-sm uppercase tracking-wide">
                    {{ getPropertyLabel(propertyName) }}
                  </span>
                  <q-btn
                    size="sm"
                    flat
                    round
                    icon="close"
                    @click="unsubscribeSingleProperty(propertyName)"
                    class="text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                  />
                </div>
                <div class="text-2xl font-bold text-blue-700 dark:text-blue-300 mb-2 font-mono">
                  {{ formatValue(data.value) }}
                </div>
                <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <div class="flex items-center gap-1">
                    <q-icon name="schedule" size="14px" />
                    <span>
                      {{
                        data.timestamp
                          ? new Date(data.timestamp).toLocaleTimeString()
                          : "--"
                      }}
                    </span>
                  </div>
                  <span
                    :class="
                      data.status === 'OK'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    "
                    class="font-semibold"
                  >
                    {{ data.status || "--" }}
                  </span>
                </div>
                <div
                  v-if="data.type"
                  class="mt-2 pt-2 border-t border-blue-200 dark:border-blue-700/50"
                >
                  <q-chip
                    :color="getEventTypeChipColor(data.type)"
                    text-color="white"
                    size="sm"
                    :label="getEventTypeLabel(data.type)"
                  />
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div
              v-if="Object.keys(subscribedData).length === 0"
              class="text-center py-12 text-gray-500 dark:text-gray-400"
            >
              <q-icon name="inbox" size="64px" class="mb-4 opacity-50" />
              <p class="text-lg">暂无数据</p>
              <p class="text-sm mt-2">订阅属性后，数据将在这里显示</p>
            </div>
          </div>
        </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, onMounted } from "vue";
import { useQuasar } from "quasar";
import CarPropertyClientClient, {
  CarPropertyValue,
  SubscribeOptionFlags,
} from "@/utils/capacitor/carPropertyClientPlugin";
import { isNative } from "@/utils/capacitor";

const $q = useQuasar();

// 状态
const isLoading = ref(false);
const error = ref<string | null>(null);
const isConnected = ref(false);

// Get Property
const selectedGetProperties = ref<string[]>([]);
const getResults = ref<CarPropertyValue[]>([]);

// Set Property
const selectedSetProperties = ref<string[]>([]);
const setValueInput = ref("");
const setValueType = ref<"float" | "int" | "boolean" | "string">("float");
const setResults = ref<
  Array<{ propertyName: string; success: boolean; error?: string }>
>([]);

// Subscribe
const selectedSubscribeProperties = ref<string[]>([]);
const updateRateMs = ref<number>(50);
const selectedEventFlags = ref<number[]>([SubscribeOptionFlags.UPDATE]);
const subscribedProperties = ref<string[]>([]);
const subscribedData = ref<Record<string, CarPropertyValue>>({});

// 可用属性列表
const availableProperties = [
  { label: "车速", value: "CAN::VehSpd" },
  { label: "显示车速", value: "CAN::VehDisplaySpd" },
  { label: "档位", value: "CAN::ActuGearShiftPos" },
  { label: "空调开关", value: "CAN::HuFrtACSwReq" },
  { label: "INS 纬度", value: "SOA::INSInfo_MSG_InsLatitude" },
  { label: "INS 经度", value: "SOA::INSInfo_MSG_InsLongitude" },
  { label: "INS 航向角", value: "SOA::INSInfo_MSG_InsHeadingAngle" },
  { label: "GNSS 纬度", value: "SOA::GNSSLOCInfo_MSG_GNSSLOCLatitude" },
  { label: "GNSS 经度", value: "SOA::GNSSLOCInfo_MSG_GNSSLOCLongitude" },
  { label: "定位数据 INS 纬度", value: "SOA::PositioningData_MSG_PositiongingData_MSG_InsLatitude" },
  { label: "定位数据 INS 经度", value: "SOA::PositioningData_MSG_PositiongingData_MSG_InsLongitude" },
  { label: "方向盘转角 (SOA)", value: "SOA::EPSModeSts_MSG_EPSStrgAng" },
  { label: "悬架 IMU 垂直加速度", value: "SOA::SuspensionSensorInfo_MSG_SusIMUAccVert" },
  { label: "悬架左前轮加速度", value: "SOA::SuspensionSensorInfo_MSG_SusWhlAccFL" },
  { label: "悬架右前轮加速度", value: "SOA::SuspensionSensorInfo_MSG_SusWhlAccFR" },
  { label: "悬架控制器温度 A", value: "SOA::SuspensionSensorInfo_MSG_SusCtrlrTempA" },
  { label: "悬架执行器高度等级", value: "SOA::SuspensionSts_MSG_SusActuHghtLvl" },
  { label: "悬架执行器腔室模式", value: "SOA::SuspensionSts_MSG_SusActuChmbMd" },
  { label: "MATRIX_BLECAN_VERSION", value: "STATIC::MATRIX_BLECAN_VERSION" },
  { label: "MATRIX_LIN7_VERSION", value: "STATIC::MATRIX_LIN7_VERSION" },
  { label: "MATRIX_LIN8_VERSION", value: "STATIC::MATRIX_LIN8_VERSION" },
  { label: "MATRIX_MCU_VERSION", value: "STATIC::MATRIX_MCU_VERSION" },
  { label: "MATRIX_ROUTE_VERSION", value: "STATIC::MATRIX_ROUTE_VERSION" },
  { label: "MATRIX_SOA_VERSION", value: "STATIC::MATRIX_SOA_VERSION" },
  { label: "MATRIX_ZCAN_VERSION", value: "STATIC::MATRIX_ZCAN_VERSION" },
];

// 值类型选项
const valueTypeOptions = [
  { label: "浮点数", value: "float" },
  { label: "整数", value: "int" },
  { label: "布尔值", value: "boolean" },
  { label: "字符串", value: "string" },
];

// 事件标志选项
const eventFlagOptions = [
  { label: "UPDATE (更新)", value: SubscribeOptionFlags.UPDATE },
  { label: "SET (设置)", value: SubscribeOptionFlags.SET },
  { label: "SET_ERROR (设置错误)", value: SubscribeOptionFlags.SET_ERROR },
  { label: "ON_CHANGE (变化时)", value: SubscribeOptionFlags.ON_CHANGE },
  { label: "NEED_CURRENT_VALUE (需要当前值)", value: SubscribeOptionFlags.NEED_CURRENT_VALUE },
];

// 从选择的属性列表中获取属性值，兼容 q-select 对象值
const getPropsFromSelected = (selected: Array<string | { value?: string }>) => {
  return (selected || [])
    .map((item) => (typeof item === "string" ? item : item?.value || ""))
    .filter((s) => !!s);
};

// 计算属性
const isMultipleSet = computed(() => {
  return selectedSetProperties.value.length > 1;
});

// 全选/取消全选功能
const selectAllGetProperties = () => {
  if (selectedGetProperties.value.length === availableProperties.length) {
    selectedGetProperties.value = [];
  } else {
    selectedGetProperties.value = availableProperties.map((p) => p.value);
  }
};

const selectAllSetProperties = () => {
  if (selectedSetProperties.value.length === availableProperties.length) {
    selectedSetProperties.value = [];
  } else {
    selectedSetProperties.value = availableProperties.map((p) => p.value);
  }
};

const selectAllSubscribeProperties = () => {
  if (selectedSubscribeProperties.value.length === availableProperties.length) {
    selectedSubscribeProperties.value = [];
  } else {
    selectedSubscribeProperties.value = availableProperties.map((p) => p.value);
  }
};


// 检查连接状态
const checkConnection = async () => {
  if (!isNative) {
    $q.notify({
      type: "warning",
      message: "此功能仅在原生应用中可用",
      position: "top",
    });
    return;
  }

  try {
    const result = await CarPropertyClientClient.isConnected();
    isConnected.value = result.connected;
  } catch (err) {
    console.error("检查连接状态失败:", err);
    $q.notify({
      type: "negative",
      message: "检查连接状态失败: " + (err as Error).message,
      position: "top",
    });
  }
};

// 获取属性
const handleGetProperty = async () => {
  if (!isNative) {
    $q.notify({
      type: "warning",
      message: "此功能仅在原生应用中可用",
      position: "top",
    });
    return;
  }

  isLoading.value = true;
  error.value = null;
  getResults.value = [];

  try {
    const props = getPropsFromSelected(selectedGetProperties.value);
    if (props.length === 0) {
      $q.notify({
        type: "warning",
        message: "请选择属性名称",
        position: "top",
      });
      return;
    }

    const result = await CarPropertyClientClient.getProperty(
      props.length === 1
        ? { propertyName: props[0] }
        : { propertyNames: props }
    );
    console.log("[CarPropertyClientTest] getProperty result:", result);

    if ("properties" in result) {
      getResults.value = result.properties;
    } else {
      getResults.value = [result];
    }

    $q.notify({
      type: "positive",
      message: `成功获取 ${getResults.value.length} 个属性`,
      position: "top",
    });
  } catch (err) {
    error.value = (err as Error).message;
    $q.notify({
      type: "negative",
      message: "获取属性失败: " + (err as Error).message,
      position: "top",
    });
  } finally {
    isLoading.value = false;
  }
};

// 设置属性
const handleSetProperty = async () => {
  if (!isNative) {
    $q.notify({
      type: "warning",
      message: "此功能仅在原生应用中可用",
      position: "top",
    });
    return;
  }

  isLoading.value = true;
  error.value = null;
  setResults.value = [];

  try {
    const props = getPropsFromSelected(selectedSetProperties.value);
    if (props.length === 0) {
      $q.notify({
        type: "warning",
        message: "请选择属性名称",
        position: "top",
      });
      return;
    }

    if (!setValueInput.value) {
      $q.notify({
        type: "warning",
        message: "请输入属性值",
        position: "top",
      });
      return;
    }

    // 解析值
    let values: any[] = [];
    if (props.length > 1) {
      // 多个属性，解析多个值
      values = setValueInput.value
        .split(",")
        .map((s) => s.trim())
        .map((v) => {
          if (setValueType.value === "int") return parseInt(v, 10);
          if (setValueType.value === "float") return parseFloat(v);
          if (setValueType.value === "boolean") return v === "true" || v === "1";
          return v;
        });
    } else {
      // 单个属性
      const val = setValueInput.value.trim();
      if (setValueType.value === "int") values = [parseInt(val, 10)];
      else if (setValueType.value === "float") values = [parseFloat(val)];
      else if (setValueType.value === "boolean") values = [val === "true" || val === "1"];
      else values = [val];
    }

    const result = await CarPropertyClientClient.setProperty(
      props.length === 1
        ? {
            propertyName: props[0],
            value: values[0],
            valueType: setValueType.value,
          }
        : {
            propertyNames: props,
            values: values,
            valueType: setValueType.value,
          }
    );

    if ("results" in result) {
      setResults.value = result.results;
    } else {
      setResults.value = [result];
    }

    const successCount = setResults.value.filter((r) => r.success).length;
    $q.notify({
      type: successCount === setResults.value.length ? "positive" : "warning",
      message: `设置完成：${successCount}/${setResults.value.length} 成功`,
      position: "top",
    });
  } catch (err) {
    error.value = (err as Error).message;
    $q.notify({
      type: "negative",
      message: "设置属性失败: " + (err as Error).message,
      position: "top",
    });
  } finally {
    isLoading.value = false;
  }
};

// 订阅属性
const handleSubscribe = async () => {
  if (!isNative) {
    $q.notify({
      type: "warning",
      message: "此功能仅在原生应用中可用",
      position: "top",
    });
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const props = getPropsFromSelected(selectedSubscribeProperties.value);
    if (props.length === 0) {
      $q.notify({
        type: "warning",
        message: "请选择属性名称",
        position: "top",
      });
      return;
    }

    // 计算事件标志
    const eventFlags = selectedEventFlags.value.reduce((acc, flag) => acc | flag, 0);

    // 订阅，直接传入 callback
    await CarPropertyClientClient.subscribeProperty(
      {
        propertyNames: props.length > 1 ? props : undefined,
        propertyName: props.length === 1 ? props[0] : undefined,
        updateRateMs: updateRateMs.value,
        eventFlags: eventFlags,
      },
      (data: CarPropertyValue) => {
        console.log("[CarPropertyClient] 收到数据:", data);
        // 更新对应属性的数据，同一个属性只保留最新值
        if (data.propertyName) {
          subscribedData.value[data.propertyName] = data;
        }
      }
    );

    // 更新已订阅列表
    for (const prop of props) {
      if (!subscribedProperties.value.includes(prop)) {
        subscribedProperties.value.push(prop);
      }
    }

    $q.notify({
      type: "positive",
      message: `成功订阅 ${props.length} 个属性`,
      position: "top",
    });
  } catch (err) {
    error.value = (err as Error).message;
    $q.notify({
      type: "negative",
      message: "订阅失败: " + (err as Error).message,
      position: "top",
    });
  } finally {
    isLoading.value = false;
  }
};

// 取消订阅
const handleUnsubscribe = async () => {
  if (!isNative) {
    $q.notify({
      type: "warning",
      message: "此功能仅在原生应用中可用",
      position: "top",
    });
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const props = getPropsFromSelected(selectedSubscribeProperties.value);
    
    if (props.length === 0 && subscribedProperties.value.length > 0) {
      // 如果没有选择，取消所有已订阅的
      await CarPropertyClientClient.unsubscribeProperty({
        propertyNames: subscribedProperties.value,
      });
      subscribedProperties.value = [];
      subscribedData.value = {};
    } else if (props.length > 0) {
      // 取消选中的
      await CarPropertyClientClient.unsubscribeProperty(
        props.length === 1
          ? { propertyName: props[0] }
          : { propertyNames: props }
      );
      // 从已订阅列表中移除
      subscribedProperties.value = subscribedProperties.value.filter(
        (p) => !props.includes(p)
      );
      // 从订阅数据中移除
      for (const prop of props) {
        delete subscribedData.value[prop];
      }
    }

    $q.notify({
      type: "positive",
      message: "取消订阅成功",
      position: "top",
    });
  } catch (err) {
    error.value = (err as Error).message;
    $q.notify({
      type: "negative",
      message: "取消订阅失败: " + (err as Error).message,
      position: "top",
    });
  } finally {
    isLoading.value = false;
  }
};

// 移除所有监听器
const handleRemoveAllListeners = async () => {
  if (!isNative) {
    $q.notify({
      type: "warning",
      message: "此功能仅在原生应用中可用",
      position: "top",
    });
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    await CarPropertyClientClient.removeAllListeners();
    subscribedProperties.value = [];
    subscribedData.value = {};
    $q.notify({
      type: "positive",
      message: "已移除所有监听器",
      position: "top",
    });
  } catch (err) {
    error.value = (err as Error).message;
    $q.notify({
      type: "negative",
      message: "移除所有监听器失败: " + (err as Error).message,
      position: "top",
    });
  } finally {
    isLoading.value = false;
  }
};

// 获取属性标签
const getPropertyLabel = (propertyName: string): string => {
  const prop = availableProperties.find((p) => p.value === propertyName);
  return prop?.label || propertyName;
};

// 取消单个属性订阅
const unsubscribeSingleProperty = async (propertyName: string) => {
  if (!isNative) {
    $q.notify({
      type: "warning",
      message: "此功能仅在原生应用中可用",
      position: "top",
    });
    return;
  }

  try {
    await CarPropertyClientClient.unsubscribeProperty({
      propertyName: propertyName,
    });
    
    // 从已订阅列表中移除
    subscribedProperties.value = subscribedProperties.value.filter(
      (p) => p !== propertyName
    );
    
    // 从订阅数据中移除
    delete subscribedData.value[propertyName];

    $q.notify({
      type: "positive",
      message: `已取消订阅 ${getPropertyLabel(propertyName)}`,
      position: "top",
    });
  } catch (err) {
    console.error("取消订阅失败:", err);
    $q.notify({
      type: "negative",
      message: "取消订阅失败: " + (err as Error).message,
      position: "top",
    });
  }
};

// 清空订阅数据
const clearSubscriptionData = () => {
  subscribedData.value = {};
  $q.notify({
    type: "info",
    message: "已清空数据",
    position: "top",
  });
};

// 格式化值
const formatValue = (value: any): string => {
  if (value === null || value === undefined) {
    return "--";
  }
  if (typeof value === "number") {
    if (value % 1 !== 0) {
      return value.toFixed(6);
    }
    return value.toString();
  }
  if (Array.isArray(value)) {
    return `[${value.join(", ")}]`;
  }
  return String(value);
};

// 获取事件类型标签
const getEventTypeLabel = (type?: string): string => {
  const labels: Record<string, string> = {
    update: "更新",
    set: "设置",
    setError: "设置错误",
  };
  return labels[type || "update"] || type || "未知";
};

// 获取事件类型芯片颜色
const getEventTypeChipColor = (type?: string): string => {
  const colors: Record<string, string> = {
    update: "blue",
    set: "green",
    setError: "red",
  };
  return colors[type || "update"] || "grey";
};

// 组件挂载时检查连接状态
onMounted(() => {
  checkConnection();
});

// 组件卸载时清理
onUnmounted(async () => {
  // 清理所有订阅
  try {
    await CarPropertyClientClient.removeAllListeners();
  } catch (error) {
    console.error("组件卸载时清理订阅失败:", error);
  }
});
</script>

<style scoped>
/* 自定义滚动条样式 */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>

